


















































































































import { Component } from "vue-property-decorator";

import { RingLoader } from "vue-spinners-css";

import { BaseRootPageComponent } from "../BaseRootPageComponent";
import PasswordRule from "./PasswordRule.vue";
import { PasswordRules } from "@/models";
import _gateway from "@/utils/gateway";
import { updateGTMGlobalVariables } from "@/utils/gtm";
import { AxiosResponse } from "axios";

@Component({
  components: { PasswordRule, RingLoader }
})
export default class ChangePassword extends BaseRootPageComponent {
  // Use a class property for dependencies so unit tests can override them
  gateway = _gateway;

  isLoading = true;
  rules!: PasswordRules;
  currentPassword = "";
  newPassword = "";
  confirmPassword = "";

  // FYI if these properties aren't defined, Vue won't track them correctly
  readonly errors = {
    currentPassword: "",
    newPassword: {
      length: undefined as boolean | undefined,
      lowercase: undefined as boolean | undefined,
      uppercase: undefined as boolean | undefined,
      number: undefined as boolean | undefined,
      special: undefined as boolean | undefined,
      history: undefined as boolean | undefined,
      other: undefined as string | undefined
    },
    confirmPassword: "",
    other: ""
  };

  get hasErrors() {
    return this.errors.currentPassword || this.isNewPasswordInvalid || this.errors.confirmPassword || this.errors.other;
  }

  get isNewPasswordInvalid() {
    const errors = this.errors.newPassword;
    return (
      errors.length ||
      errors.lowercase ||
      errors.uppercase ||
      errors.number ||
      errors.special ||
      errors.history ||
      errors.other
    );
  }

  created() {
    this.setTitle("Change Password");
    // updateGTMGlobalVariables(this);
  }

  async mounted() {
    // FYI if this fails, the user will automatically be redirected to the error page
    this.rules = await this.gateway.getPasswordRules();
    this.isLoading = false;
  }

  cancel() {
    this.$router.back();
  }

  async submit() {
    this.validate();
    if (this.hasErrors) return;

    try {
      this.isLoading = true;
      await this.gateway.changePassword(this.currentPassword, this.newPassword, this.confirmPassword);
      this.$router.back();
    } catch (err) {
      const error = ((err as AxiosResponse)?.data as string) || "An unknown error occurred.";

      // Find out which field the error belongs to
      const e = error.toLowerCase();
      if (e.indexOf("current password") >= 0) {
        this.errors.currentPassword = error;
      } else if (e.indexOf("confirm password") >= 0) {
        this.errors.confirmPassword = error;
      }
      // If a match wasn't found on current, confirm, or new, then treat errors with "password" as new password
      else if (e.indexOf("new password") >= 0 || e.indexOf("password") >= 0) {
        // If the issue is because the password matches one of the previous passwords, show the history error instead
        // of showing a new rule
        if (e.indexOf("new password must be different") >= 0) {
          this.errors.newPassword.history = true;
        } else {
          this.errors.newPassword.other = error;
        }
      } else {
        this.errors.other = error;
      }
    } finally {
      this.isLoading = false;
    }
  }

  validate() {
    this.validateCurrentPassword();
    this.validateNewPassword();
    this.validateConfirmPassword();
    this.errors.other = "";
  }

  validateCurrentPassword() {
    const getError = (): string => {
      if (!this.currentPassword) return "Current password is required.";

      return "";
    };

    this.errors.currentPassword = getError();
  }

  validateNewPassword() {
    const errors = this.errors.newPassword;
    const password = this.newPassword ?? "";

    errors.length = password.length < this.rules.minLength || password.length > this.rules.maxLength;
    errors.lowercase = !password.match(/[a-z]/);
    errors.uppercase = !password.match(/[A-Z]/);
    errors.number = !password.match(/\d/);
    errors.special = !password.match(/[!@#$%^&*+\-?.,]/);

    // Since the user changed the password, we don't know if it's the same as an existing password, so reset the
    // history error
    errors.history = undefined;

    // The only characters RKP doesn't allow are: <, >, and _. If the user is using these, let them know
    const matches = password.match(/[<>_]/g);
    if (matches && matches?.length > 0) {
      const characters = matches.reduce((result, value) => {
        if (result.indexOf(value) === -1) result.push(value);
        return result;
      }, [] as string[]);

      errors.other = "Cannot contain: " + characters.join(" ");
    } else {
      errors.other = "";
    }
  }

  validateConfirmPassword() {
    const getError = (): string => {
      if (!this.confirmPassword) return "Confirm password is required.";

      if (this.confirmPassword != this.newPassword) {
        return "Confirm password must match the new password.";
      }

      return "";
    };

    this.errors.confirmPassword = getError();
  }
}
